import { oneWords, textWords, type WizardRefusalWording } from "@/base";
import { ApiError, apiErrorCode, apiErrorReason } from "@/lib/api";
import type {
  AccountDetail,
  AccountInput,
  EntityType,
} from "@/lib/brokerAccounts";
import type { ManifestField, ProductManifest } from "@/lib/brokerProducts";
import { ENTITY_TYPE_OPTIONS } from "@/lib/entityTypes";
import { normalizeUsPhone, primaryPhone, withPrimaryNumber } from "@/lib/phone";
import type { SubmissionDetail } from "@/lib/submissions/brokerSubmissions";
import { phoneWords } from "@/lib/submissions/wc/contacts";
import { isEmailShaped } from "@/lib/email";

// The Workers' Comp wizard's Basic information (BRK-S301, BRK-S388), as pure
// rules: what it reads back from PMS, what it checks before saving, and what
// it writes. The account holds the insured (legal name, DBA, FEIN, entity,
// email, phone, the date the business started); the submission's field bag
// holds the effective date and the manifest's Basic information answers
// (questions.ts). An existing insured is read-only here (Nav, 2026-10-08):
// see `insuredEditable`. The industry isn't asked: it has no use yet (Tyler,
// 2026-10-21).

/** The Business step's values, every one a string so its inputs stay
 *  controlled. */
export interface WcBusiness {
  legalName: string;
  /** The name it does business as, if any (BRK-S306): optional, and sent to
   *  Great American as `doingBusinessAs` (PMS-S716). */
  dbaName: string;
  /** USPS code. Set when the application starts; afterwards it changes with
   *  the mailing address, on the Locations step. */
  state: string;
  /** As shown: 12-3456789. Sent as the digits alone (D-10). */
  ein: string;
  entityType: EntityType | "";
  emailAddress: string;
  /** The business's primary number, as typed. */
  phone: string;
  /** When the business started (BRK-S388): the insured's, ISO YYYY-MM-DD on
   *  the wire, shown by the date picker (D-10). */
  businessStartDate: string;
  /** ISO YYYY-MM-DD on the wire; the date picker shows it locally (D-10). */
  effectiveDate: string;
}

export const EMPTY_BUSINESS: WcBusiness = {
  legalName: "",
  dbaName: "",
  state: "",
  ein: "",
  entityType: "",
  emailAddress: "",
  phone: "",
  businessStartDate: "",
  effectiveDate: "",
};

/** The legal entities Workers' Comp offers: every one but a joint venture,
 *  which it doesn't write (BRK-S388, the 10/20 call). Other lines offer them
 *  all. */
export const WC_ENTITY_TYPE_OPTIONS = ENTITY_TYPE_OPTIONS.filter(
  (o) => o.value !== "joint_venture",
);

const offeredForWc = (entityType: WcBusiness["entityType"]) =>
  WC_ENTITY_TYPE_OPTIONS.some((o) => o.value === entityType);

/** The date the business started, as the manifest declares it (PMS-S808): its
 *  label and its type (D-10 rule 1). Null when the manifest doesn't ask it, as
 *  a PMS before PMS-S808 doesn't. */
export function startDateField(
  manifest: ProductManifest | null,
): ManifestField | null {
  return (
    manifest?.fields.find(
      (f) => f.key === "businessStartDate" && f.scope === "account",
    ) ?? null
  );
}

/** What the manifest asks of the insured beyond the fixed fields. */
export interface BusinessAsks {
  askStartDate: boolean;
}

/** The FEIN's digits alone: what PMS stores and the carriers receive. */
export function einDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, 9);
}

/** The FEIN as shown: 12-3456789, from whatever spelling was typed. */
export function formatEin(value: string): string {
  const digits = einDigits(value);
  return digits.length > 2
    ? `${digits.slice(0, 2)}-${digits.slice(2)}`
    : digits;
}

/** The Business step as PMS holds it. A value PMS doesn't hold reads blank. */
export function businessFrom(
  account: AccountDetail,
  detail: SubmissionDetail,
): WcBusiness {
  const bagDate = detail.data.effectiveDate;
  return {
    legalName: account.legalName,
    dbaName: account.dbaName ?? "",
    state:
      account.mailingAddress?.state ||
      account.mailingState ||
      account.legalAddress?.state ||
      "",
    ein: formatEin(account.ein ?? ""),
    entityType: account.entityType ?? "",
    emailAddress: account.emailAddress ?? "",
    phone: primaryPhone(account.phones)?.number ?? "",
    businessStartDate: account.businessStartDate ?? "",
    effectiveDate:
      (typeof bagDate === "string" ? bagDate : null) ??
      detail.effectiveDate ??
      "",
  };
}

/**
 * Whether the wizard may write the insured (Nav, 2026-10-08): an existing
 * account's information changes only by endorsement, never from a new
 * submission, because it has to match the bonds and policies issued on paper.
 * An insured this submission created, with nothing else on record, is still
 * being built with it, so the broker edits it in real time, active or not.
 *
 * PMS says which (`insuredEditable`, PMS-S741). A PMS before that says
 * nothing, and the account's draft status stands in, as it did: PMS makes a
 * draft active on its first save. A status PMS didn't send reads as active,
 * the safe side of a rule about paper.
 */
export function insuredEditable(
  account: AccountDetail | null,
  submission: SubmissionDetail | null,
): boolean {
  if (account === null) return true;
  return submission?.insuredEditable ?? account.status === "draft";
}

/** The effective date's check, worded as the manifest form words it. ISO
 *  dates compare correctly as strings. */
function effectiveDateError(value: string, today: string): string | null {
  if (!value) return "Choose the effective date.";
  if (value < today) return "Effective date must be today or later.";
  return null;
}

// The step's sentences, said alike by its checks and for PMS's (BRK-S339).
const LEGAL_NAME = "Enter the insured's legal name.";
const FEIN = "Enter the 9-digit FEIN.";
const ENTITY = "Choose the legal entity.";
const EMAIL = "Enter the business email.";
const EMAIL_SHAPE = "Enter a valid email address.";
const PHONE = "Enter the business phone number.";
const START_DATE = "Choose the date the business started.";

/** The start date's check, when the manifest asks it (BRK-S388). */
function startDateError(b: WcBusiness, asks: BusinessAsks): string | null {
  return asks.askStartDate && !b.businessStartDate ? START_DATE : null;
}

/** The step's own checks, in the portal's words; {} when it may be saved.
 *  Required: what the draft needs (name, state) and what the WC carriers'
 *  floors ask of the insured (FEIN, entity, email, phone, the date the
 *  business started when the manifest asks it, PMS-S808) and of the
 *  submission (the effective date). */
export function validateBusiness(
  b: WcBusiness,
  today: string,
  asks: BusinessAsks,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!b.legalName.trim()) errors.legalName = LEGAL_NAME;
  if (!b.state) errors.state = "Choose the state.";
  if (einDigits(b.ein).length !== 9) errors.ein = FEIN;
  if (!b.entityType || !offeredForWc(b.entityType)) {
    errors.entityType = ENTITY;
  }
  const email = b.emailAddress.trim();
  if (!email) errors.emailAddress = EMAIL;
  else if (!isEmailShaped(email)) errors.emailAddress = EMAIL_SHAPE;
  if (!b.phone.trim()) errors.phone = PHONE;
  else {
    const phone = normalizeUsPhone(b.phone);
    if (!phone.ok) errors.phone = phone.reason;
  }
  const started = startDateError(b, asks);
  if (started) errors.businessStartDate = started;
  const date = effectiveDateError(b.effectiveDate, today);
  if (date) errors.effectiveDate = date;
  return errors;
}

/** What Next checks: everything while the insured is editable. Once it is on
 *  record, only what the step can still write: the submission's own effective
 *  date, and a start date the insured lacks, filled once (BRK-S388). */
export function validateBusinessStep(
  b: WcBusiness,
  editable: boolean,
  today: string,
  asks: BusinessAsks,
): Record<string, string> {
  if (editable) return validateBusiness(b, today, asks);
  const errors: Record<string, string> = {};
  const started = startDateError(b, asks);
  if (started) errors.businessStartDate = started;
  const date = effectiveDateError(b.effectiveDate, today);
  if (date) errors.effectiveDate = date;
  return errors;
}

/** What PMS's checks of the account write refuse, in the step's words
 *  (BRK-S339): the fields `businessAccountPatch` and `startDatePatch` send.
 *  The phone is named by its list path, and shows on the phone. */
export const BUSINESS_REFUSALS: WizardRefusalWording = {
  legalName: textWords(LEGAL_NAME, "legal name"),
  dbaName: textWords("Enter the DBA name, or leave it blank.", "DBA name"),
  ein: oneWords(FEIN),
  entityType: oneWords(ENTITY),
  emailAddress: {
    required: EMAIL,
    too_short: EMAIL,
    too_long: "Shorten the business email.",
    invalid: EMAIL_SHAPE,
  },
  "phones.*.number": phoneWords(PHONE),
  businessStartDate: oneWords(START_DATE),
};

/**
 * The account write: the legal name and only what changed. PMS's broker PUT
 * leaves an absent field alone, so nothing the step doesn't show can be wiped
 * (BRK-S223). Phones are the exception: a sent list replaces the account's, so
 * the primary is changed in place and every other number is carried.
 */
export function businessAccountPatch(
  b: WcBusiness,
  account: AccountDetail,
): AccountInput {
  const input: AccountInput = { legalName: b.legalName.trim() };
  const dba = b.dbaName.trim();
  if (dba && dba !== (account.dbaName ?? "")) input.dbaName = dba;
  const ein = einDigits(b.ein);
  if (ein && ein !== einDigits(account.ein ?? "")) input.ein = ein;
  if (b.entityType && b.entityType !== account.entityType) {
    input.entityType = b.entityType;
  }
  const email = b.emailAddress.trim();
  if (email && email !== (account.emailAddress ?? "")) {
    input.emailAddress = email;
  }
  const phone = b.phone.trim();
  const current = primaryPhone(account.phones)?.number ?? "";
  if (phone && phone !== current) {
    input.phones = withPrimaryNumber(account.phones, phone);
  }
  if (
    b.businessStartDate &&
    b.businessStartDate !== (account.businessStartDate ?? "")
  ) {
    input.businessStartDate = b.businessStartDate;
  }
  return input;
}

/**
 * The one write to an insured on record (BRK-S388; Nav, 2026-10-21/22): a
 * start date it lacks, filled once. Only that, beside the legal name PMS's
 * write requires, unchanged. Null when there's nothing to fill: the insured
 * holds one already, which changes only by endorsement, or none was given.
 */
export function startDatePatch(
  b: WcBusiness,
  account: AccountDetail,
): AccountInput | null {
  if (account.businessStartDate || !b.businessStartDate) return null;
  return {
    legalName: account.legalName,
    businessStartDate: b.businessStartDate,
  };
}

/** PMS-S818's refusal of a FEIN change once the account has a binder or
 *  policy, in the portal's words. */
export const EIN_LOCKED = "EIN_LOCKED";
export const EIN_LOCKED_COPY =
  "The FEIN can't change once the account has a binder or policy. Contact Signal to correct it.";

/** The other reasons a FEIN is locked (`details.reason`), each said as PMS
 *  says it: an open bind request (PMS-S663) is no binder, nor is the
 *  account's surety (PMS-S825). A reason not here reads as a binder's. */
export const EIN_LOCKED_REASON_COPY: Readonly<Record<string, string>> = {
  bind_requested:
    "The FEIN can't change while a bind request is open on the account. Contact Signal to correct it.",
  surety:
    "The FEIN can't change once the account has a surety approval, a signed GIA or an executed bond. Contact Signal to correct it.",
};

/** A refused account write, ready for the wizard: a locked FEIN (PMS-S818)
 *  lands on the FEIN through the wizard's one channel, its field errors,
 *  with why it's locked; any other refusal is left as it was. */
export function businessRefusal(err: unknown): unknown {
  if (!(err instanceof ApiError) || apiErrorCode(err) !== EIN_LOCKED) {
    return err;
  }
  const reason = apiErrorReason(err);
  const copy =
    (reason !== null && Object.hasOwn(EIN_LOCKED_REASON_COPY, reason)
      ? EIN_LOCKED_REASON_COPY[reason]
      : undefined) ?? EIN_LOCKED_COPY;
  return new ApiError(400, copy, {
    error: copy,
    details: { fieldErrors: { ein: copy } },
  });
}

/** The submission's part of the step: its field bag. */
export function businessDetails(b: WcBusiness): { effectiveDate: string } {
  return { effectiveDate: b.effectiveDate };
}
